import { db } from "../firebase";
import { doc, collection, getDoc, setDoc, updateDoc, runTransaction } from "firebase/firestore";
import { TENANT_ENABLED, getActiveCompany } from "./tenant";

// مرآة orders_v2 تُحمَّل عند أول كتابة طلب فقط (import ديناميكي): هذا الملف
// مشترك مع المتجر العام، وزائر المتجر لا يجب أن يحمّل كود تعديل الطلبات في أول
// تحميل. المسار غير متزامن أصلاً (ينتظر معاملة Firestore) فلا تأخير محسوس.
const _mirrorOrdersV2 = async (orders, deletedNums) => {
  try {
    const m = await import("./ordersV2");
    return await m.mirrorOrdersV2(orders, deletedNums);
  } catch (e) { console.warn("[orders_v2 mirror]", e?.message || e); return false; }
};

// يحوّل زوج (المجموعة، المعرّف) إلى مرجع وثيقة في Firestore.
// في وضع العزل تعيش كل وثيقة تحت companies/{companyId}/... فتنعزل
// بيانات كل شركة تلقائياً. عند إيقاف العزل يبقى المسار المسطّح كما هو.
const docRef = (col, id) =>
  TENANT_ENABLED
    ? doc(db, "companies", getActiveCompany(), col, id)
    : doc(db, col, id);

// نسخ معزولة من doc()/collection() لمواقع الوصول المباشر (تتجاوز fbGet/fbSet).
// تقبل مساراً متعدد المقاطع (للمجموعات الفرعية) عبر ...rest.
// مع إيقاف العزل تتطابق تماماً مع doc(db,...)/collection(db,...) — صفر تغيير سلوكي.
export const tdoc = (col, ...rest) =>
  TENANT_ENABLED
    ? doc(db, "companies", getActiveCompany(), col, ...rest)
    : doc(db, col, ...rest);

export const tcol = (col, ...rest) =>
  TENANT_ENABLED
    ? collection(db, "companies", getActiveCompany(), col, ...rest)
    : collection(db, col, ...rest);

// معالج أخطاء موحّد لـ onSnapshot: يتجاهل رفض الصلاحيات العابر (permission-denied)
// الذي يحدث لحظة تسجيل الخروج أو قبل جاهزية توكن المصادقة — والقواعد fail-closed
// تعمل حينها بشكل صحيح — ويسجّل أي خطأ آخر. مرّره كوسيط ثالث لأي listener معزول
// للشركة كي لا يتحوّل الرفض العابر إلى خطأ عام يلتقطه Sentry.
export const snapErr = (label = "") => (err) => {
  if (err && err.code === "permission-denied") return;
  console.error("[snapshot" + (label ? ":" + label : "") + "]", err);
};

export const fbGet = async (col, id) => {
  try { const s = await getDoc(docRef(col,id)); return s.exists()?s.data():null; } catch { return null; }
};

export const fbSet = async (col, id, data) => {
  try { await setDoc(docRef(col,id),data,{merge:true}); return true; } catch { return false; }
};

// بدون merge — يستبدل الـ document كاملاً (للـ arrays مهم)
export const fbSetFull = async (col, id, data) => {
  try { await setDoc(docRef(col,id),data); return true; } catch { return false; }
};

// تحديث حقول محددة فقط (dot-notation). لا يمس باقي الحقول ولا الحقول المتداخلة الأخرى.
// مثال: fbUpdate("bekvam","product_attrs",{"productOrder.__all__":[...]})
export const fbUpdate = async (col, id, data) => {
  try { await updateDoc(docRef(col, id), data); return true; } catch { return false; }
};

// قراءة-تعديل-كتابة ذرّية: يعيد القراءة داخل transaction ويكتب ناتج mutate(الحالي).
// يمنع كاتبين متزامنين (مثلاً الويب هوك وهو يضيف طلباً) من الدوس على بعض.
export const fbTx = async (col, id, mutate) => {
  try {
    await runTransaction(db, async (tx) => {
      const ref = docRef(col, id);
      const snap = await tx.get(ref);
      const next = mutate(snap.exists() ? snap.data() : {});
      if (next !== undefined) tx.set(ref, next);
    });
    return true;
  } catch (e) {
    // كان الفشل صامتاً تماماً: المستخدم يرى التعديل (تحديث متفائل) ثم يعود مع أول
    // تحديث من السيرفر بلا أي أثر. نُسجّل السبب على الأقل، والمستدعي يفحص false.
    console.error("[fbTx]", col, id, e?.code || "", e?.message || e);
    return false;
  }
};

// ── الطلبات: كتابة ذرّية آمنة من التضارب لمصفوفة الطلبات المشتركة ───────────
// الويب هوك (السيرفر) والتطبيق كلاهما يقرأ-يعدّل-يكتب وثيقة واحدة فيها كل الطلبات.
// الكتابة الكاملة العمياء تدوس على التغييرات المتزامنة: طلبات جديدة تختفي وطلبات
// محذوفة ترجع. commitOrders يعيد القراءة داخل transaction ويطبّق فقط الفرق بين
// baseline (ما قرأه العميل) و result (ما يريده) — حسب مفتاح ثابت للطلب — على
// المصفوفة الطازجة من السيرفر، فتُحفظ كتابات الويب هوك المتزامنة. كما يضيف أرقام
// الطلبات المحذوفة إلى _deleted (شاهدة قبر) كي لا يعيدها الويب هوك لاحقاً.
const _oKey = (o) => o?.id != null ? "id:" + o.id : (o?.orderNum != null && o.orderNum !== "" ? "num:" + String(o.orderNum) : "");
const _stripMeta = (o) => { const { _docId, _key, _idx, _v2, ...rest } = o; return rest; };
const _eq = (a, b) => a === b || JSON.stringify(a) === JSON.stringify(b);

export const commitOrders = async (foundKey, baseline, result) => {
  const bl = baseline || [], rs = result || [];
  const resultByKey = new Map();
  rs.forEach(o => { const k = _oKey(o); if (k) resultByKey.set(k, o); });
  const baseKeys = new Set();
  const baselineByKey = new Map();
  bl.forEach(o => { const k = _oKey(o); if (k) { baseKeys.add(k); baselineByKey.set(k, o); } });
  // طلبات مصدرها orders_v2 — تُعرَف من baseline أيضاً لا من result وحده: بعض
  // المستدعين (السائق/الخيّاط) يمرّرون في result الحقول المتغيّرة فقط، بلا
  // العلامة _v2 التي يحملها الطلب الأصلي.
  const v2Keys = new Set();
  [...bl, ...rs].forEach(o => { const k = _oKey(o); if (k && o?._v2) v2Keys.add(k); });
  // طلبات كانت في baseline واختفت من result = حُذفت عمداً
  const deletedNums = bl.filter(o => { const k = _oKey(o); return k && !resultByKey.has(k); }).map(o => o.orderNum).filter(Boolean);
  const deletedNumSet = new Set(deletedNums.map(String));
  const ok = await fbTx("orders", "webhook_orders", (cur) => {
    const key = foundKey || Object.keys(cur).find(k => Array.isArray(cur[k])) || "orders";
    const fresh = Array.isArray(cur[key]) ? cur[key] : [];
    const freshKeys = new Set();
    fresh.forEach(o => { const k = _oKey(o); if (k) freshKeys.add(k); });
    const merged = fresh
      // أبقِ الطلب إن كان معدّلاً (موجود بـ result) أو غير معروف للعميل أصلاً
      // (أُضيف بالتزامن من الويب هوك). احذف فقط ما كان بالـ baseline وأُزيل من result.
      .filter(o => { const k = _oKey(o); if (!k) return true; return resultByKey.has(k) || !baseKeys.has(k); })
      // دمج على مستوى الحقل: طبّق فقط الحقول التي غيّرها هذا العميل فعلاً (تختلف عن baseline)
      // فوق النسخة الطازجة من السيرفر، كي لا تُرجِع كتابةُ مستخدمٍ تعديلَ مستخدمٍ آخر على حقل مختلف
      // لنفس الطلب. إن لم يكن للطلب baseline معروف (أُضيف بالتزامن) نطبّق نسخة العميل كاملة.
      .map(o => {
        const k = _oKey(o);
        if (!(k && resultByKey.has(k))) return o;
        const res = _stripMeta(resultByKey.get(k));
        const base = baselineByKey.has(k) ? _stripMeta(baselineByKey.get(k)) : null;
        if (!base) return { ...o, ...res };
        const patch = {};
        for (const f of Object.keys(res)) { if (!_eq(res[f], base[f])) patch[f] = res[f]; }
        return { ...o, ...patch };
      });
    // ألحِق طلبات العميل الجديدة فعلاً (بـ result وغير موجودة بالطازجة).
    // استثناءان:
    //  • طلب قرأه العميل من orders_v2 وليس في المصفوفة أصلاً — هذا طلب خارج سعة
    //    المستند الموحّد، وإلحاقه هنا يعيد نفخ الوثيقة التي نهرب منها. تعديله
    //    يُحفظ في مرآة orders_v2 أدناه، وهي مصدر قراءته.
    //  • طلب عليه شاهدة قبر (حُذف عمداً) — نفس ما يفعله الويب هوك: لا يُبعَث من
    //    جديد. يحمي من سباق نادر: موظف يعدّل طلباً على شاشته وقد حُذف للتوّ من
    //    اللوحة، فكان تعديله يعيده صفاً ناقصاً.
    rs.forEach(o => {
      const k = _oKey(o);
      if (!k || freshKeys.has(k) || v2Keys.has(k)) return;
      if (o.orderNum != null && cur._deleted && cur._deleted[String(o.orderNum)]) return;
      merged.push(_stripMeta(o));
    });
    // حذف مُدرِك للتكرار: عند حذف طلب، امسح كل نسخه التي تحمل نفس رقم الطلب من
    // الوثيقة الطازجة — لا صفاً واحداً فقط. طلبات قديمة قد توجد مكرّرة (نفس orderNum،
    // id مختلف)؛ حذف صف واحد يترك التوأم فيعود بالظهور بعد التحديث (refresh).
    const finalArr = deletedNumSet.size
      ? merged.filter(o => !(o && o.orderNum != null && deletedNumSet.has(String(o.orderNum))))
      : merged;
    const out = { ...cur, [key]: finalArr };
    if (deletedNums.length) {
      const tomb = { ...(cur._deleted || {}) };
      deletedNums.forEach(n => { tomb[String(n)] = true; });
      out._deleted = tomb;
    }
    return out;
  });

  // ── مرآة orders_v2 ─────────────────────────────────────────────────────────
  // الويب هوك يكتب في المجموعتين؛ تعديلات اللوحة كانت تصيب المصفوفة وحدها. بعد
  // نجاح المعاملة نعكس هنا ما تغيّر فعلاً فقط (لا كل ما مرّ في result) وما حُذف،
  // كي تبقى المجموعة الجديدة صورةً صادقة للطلبات مهما كان مصدر القراءة.
  // best-effort: فشلها لا يُبطل الحفظ الذي نجح على المصفوفة.
  if (ok) {
    const changed = rs.filter(o => {
      const k = _oKey(o);
      if (!k) return false;
      const base = baselineByKey.get(k);
      return !base || !_eq(_stripMeta(o), _stripMeta(base));
    });
    await _mirrorOrdersV2(changed, deletedNums);
  }
  return ok;
};

// استرجاع طلب من النسخة الاحتياطية (orders_raw) إلى لوحة الطلبات: يضيفه إن كان
// مفقوداً (دون تكرار)، ويمسح علامة الحذف كي لا يحجبه الويب هوك أو يختفي ثانية.
export const restoreOrder = async (orderObj) => {
  // النسخة التي استقرّت في المصفوفة (المُرمَّمة أو المُعادة كما هي) — نعكسها على
  // orders_v2 بعد نجاح المعاملة كي يُرمَّم/يعود الطلب في المصدرين معاً.
  let restored = orderObj;
  const ok = await fbTx("orders", "webhook_orders", (cur) => {
    const key = Object.keys(cur).find(k => Array.isArray(cur[k])) || "orders";
    const list = Array.isArray(cur[key]) ? cur[key] : [];
    const num = String(orderObj.orderNum || "");
    const idx = list.findIndex(o => (orderObj.id != null && o.id === orderObj.id) || (num && String(o.orderNum) === num));
    let next;
    if (idx === -1) {
      // الطلب مفقود تماماً → أعِده كما وصل أول مرة.
      next = [...list, orderObj];
    } else {
      // الطلب موجود لكن قد تكون بعض الحقول (رقم الهاتف/الاسم) انمسحت أثناء تقليص
      // حجم الوثيقة (slimRaw). نرمّمها من النسخة الاحتياطية دون المساس بأي إجراء
      // شغّال على الطلب (السائق/المرحلة/الدفع/التعيينات) — نحدّث customer فقط.
      const prev = list[idx];
      const bCust = orderObj.raw?.customer || {};
      const pCust = prev.raw?.customer || {};
      // نبحث عن الرقم في كل الأماكن المعروفة داخل النسخة الاحتياطية.
      const phone = pCust.mobile || bCust.mobile || bCust.parsed_mobile?.format || bCust.phone || "";
      const name = pCust.name || bCust.name || "";
      const mergedCust = { ...bCust, ...pCust };
      // ملاحظة: Firestore يرفض القيم undefined — نضع الحقل فقط لو له قيمة، وإلا نحذفه.
      if (phone) mergedCust.mobile = phone; else delete mergedCust.mobile;
      if (name) mergedCust.name = name; else delete mergedCust.name;
      Object.keys(mergedCust).forEach(k => mergedCust[k] === undefined && delete mergedCust[k]);
      const patched = { ...prev, raw: { ...prev.raw, customer: mergedCust } };
      restored = patched;
      next = list.map((o, i) => (i === idx ? patched : o));
    }
    const tomb = { ...(cur._deleted || {}) };
    if (num) delete tomb[num];
    return { ...cur, [key]: next, _deleted: tomb };
  });
  if (ok) await _mirrorOrdersV2([restored]);
  return ok;
};
